import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError, IMG } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BadgeCheck, XCircle, Mail, Phone, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function AdminVerificationsPage() {
  const { user, ready } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/verifications?status=${tab}`)
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  if (ready && user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/verifications/${id}`, { action: "approve" });
      toast.success("Owner verified");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    setBusy(true);
    try {
      await api.post(`/admin/verifications/${rejectTarget.id}`, { action: "reject", reason: rejectReason });
      toast.success("Verification rejected");
      setRejectTarget(null); setRejectReason("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const tabs = [
    { key: "pending", label: "Pending", icon: <Clock size={14} /> },
    { key: "verified", label: "Verified", icon: <BadgeCheck size={14} /> },
    { key: "rejected", label: "Rejected", icon: <XCircle size={14} /> },
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <p className="label-eyebrow text-gray-500 mb-2">Admin</p>
      <h1 className="font-display text-4xl sm:text-5xl brand-forest mb-8">Owner verifications</h1>

      <div className="flex gap-2 mb-8 bg-[#FFEED9] rounded-full p-1 w-fit">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
              tab === t.key ? "bg-[#FF5A3D] text-white" : "text-gray-600 hover:text-[#FF5A3D]"
            }`}
          >{t.icon} {t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-brand-subtle">
          <p className="text-gray-500">Nothing here yet.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((u) => (
            <div key={u.id} className="bg-white border border-brand-subtle rounded-2xl p-5 flex flex-col md:flex-row gap-5" data-testid="admin-verification-row">
              {u.verification_doc_path ? (
                <a href={IMG(u.verification_doc_path)} target="_blank" rel="noreferrer" className="shrink-0">
                  <img src={IMG(u.verification_doc_path)} alt="ID document" className="w-full md:w-48 h-32 object-cover rounded-xl border border-brand-subtle" />
                </a>
              ) : (
                <div className="w-full md:w-48 h-32 rounded-xl bg-[#FFEED9] flex items-center justify-center text-xs text-gray-500">No doc</div>
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-display text-xl brand-forest">{u.name}</h3>
                </div>
                <p className="text-sm text-gray-600 flex items-center gap-1"><Mail size={13} /> {u.email}</p>
                {u.phone && <p className="text-sm text-gray-600 flex items-center gap-1"><Phone size={13} /> {u.phone}</p>}
                {u.verification_submitted_at && (
                  <p className="text-xs text-gray-400 mt-2">Submitted {new Date(u.verification_submitted_at).toLocaleString("en-IN")}</p>
                )}
              </div>

              {tab === "pending" && (
                <div className="flex items-center gap-2">
                  <Button
                    data-testid={`approve-${u.id}`}
                    onClick={() => approve(u.id)} disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-10 px-4"
                  >
                    <BadgeCheck size={16} className="mr-1" /> Approve
                  </Button>
                  <Button
                    data-testid={`reject-${u.id}`}
                    onClick={() => setRejectTarget(u)} variant="outline"
                    className="rounded-full h-10 px-4 border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <XCircle size={16} className="mr-1" /> Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl brand-forest">Reject verification</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">Optionally leave a reason — the owner will see this and can re-submit.</p>
          <Input
            data-testid="reject-reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Document is blurry"
          />
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setRejectTarget(null)} className="rounded-full">Cancel</Button>
            <Button
              data-testid="confirm-reject"
              onClick={submitReject} disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
            >Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
